import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// MessageList.tsx -- scrolling thread of user + assistant messages.
//
// Per design \u00a76.3 + the universal "viewer-on-right" rule the user
// articulated for symmetric chats:
//   - The CURRENT viewer's outgoing user messages are right-aligned
//     with a subtle accent background (the "self" bubble).
//   - Everyone else's messages are left-aligned: assistant replies in
//     coworker / FDN chats AND incoming messages from human peers in
//     DMs both look the same. From admin's seat alice's message lands
//     on the left; from alice's seat admin's message lands on the
//     left; everything is symmetric.
//   - Above each assistant message: ThinkingTrace (collapsible).
//   - Auto-scroll to bottom when new content lands AND user was already
//     near the bottom (don't yank them away if they're scrolling back).
//   - Citation chips render below the assistant text once complete.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ThinkingTrace } from './ThinkingTrace.js';
import { CitationChip } from './CitationChip.js';
import { ToolResultCard } from './ToolResultCard.js';
import { useAuth } from '../../hooks/useAuth.js';
/**
 * Decide which side of the panel a message renders on. The rule is
 * universal: viewer's own user messages go right ("self"); everything
 * else goes left ("other"). Assistant messages are never right-aligned
 * regardless of who initiated the session, because the assistant
 * isn't the viewer's voice.
 */
function bubbleSide(m, viewerUserId) {
    if (m.role !== 'user')
        return 'other';
    if (!viewerUserId)
        return 'other';
    if (!m.authorId) {
        // No authorId yet -- this is the in-flight optimistic placeholder
        // for the user's own send. By definition the local-typed message
        // is "self".
        return 'self';
    }
    return m.authorId === viewerUserId ? 'self' : 'other';
}
const STICK_THRESHOLD = 80; // px from bottom => "user is at the bottom"
export function MessageList({ messages, onCitationClick, projectId, aidocsFolderId }) {
    const { user } = useAuth();
    const viewerUserId = user?.userId ?? null;
    const ref = useRef(null);
    const [stickToBottom, setStickToBottom] = useState(true);
    // Track whether the user is at the bottom (auto-scroll allowed) or
    // scrolled up (don't yank).
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const onScroll = () => {
            const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
            setStickToBottom(distance < STICK_THRESHOLD);
        };
        el.addEventListener('scroll', onScroll);
        return () => el.removeEventListener('scroll', onScroll);
    }, []);
    // Auto-scroll on new content only if user is at bottom.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el || !stickToBottom)
            return;
        el.scrollTop = el.scrollHeight;
    }, [messages, stickToBottom]);
    if (messages.length === 0) {
        return (_jsx("div", { className: "message-list empty", ref: ref, children: _jsx("div", { className: "message-list-empty-hint", children: "Ask anything about your project. I can search documents, summarise, and draft new ones." }) }));
    }
    return (_jsx("div", { className: "message-list", ref: ref, children: messages.map((m) => {
            const side = bubbleSide(m, viewerUserId);
            // Render an avatar for "other"-side messages regardless of
            // whether they're an assistant reply or a human DM. The
            // glyph differs (\u2728 for FDN/coworker, \uD83D\uDC64 for
            // a human peer); for a v1 simple shape we use the assistant
            // glyph for assistant role and the person silhouette for
            // user role (since a "user" message that lands on the
            // "other" side must be a peer's incoming message).
            const otherGlyph = m.role === 'assistant' ? '\u2728' : '\uD83D\uDC64';
            return (_jsxs("div", { className: `message message-${side} message-role-${m.role}`, children: [m.role === 'assistant' && m.thinking.length > 0 && (_jsx(ThinkingTrace, { thinking: m.thinking, streaming: m.state === 'streaming' })), side === 'other' && (_jsx("div", { className: "message-avatar", "aria-hidden": true, children: otherGlyph })), _jsxs("div", { className: "message-bubble", children: [m.role === 'assistant' && m.state === 'streaming' && m.content.length === 0 ? (_jsxs("span", { className: "message-typing", children: [_jsx("span", { className: "dot" }), _jsx("span", { className: "dot" }), _jsx("span", { className: "dot" })] })) : (_jsx(RenderedContent, { content: m.content, role: m.role })), m.state === 'error' && (_jsx("div", { className: "message-error", children: m.error ?? 'Something went wrong.' })), m.role === 'assistant' && m.citations && m.citations.length > 0 && (_jsx("div", { className: "message-citations", children: m.citations.map((c, i) => (_jsx(CitationChip, { citation: c, ...(onCitationClick ? { onClick: onCitationClick } : {}) }, i))) })), m.role === 'assistant'
                                && m.generatedDocuments
                                && m.generatedDocuments.length > 0
                                && projectId
                                && (_jsx("div", { className: "message-generated-docs", children: m.generatedDocuments.map((d) => (_jsx(ToolResultCard, { doc: d, projectId: projectId, ...(aidocsFolderId ? { aidocsFolderId } : {}) }, d.documentId))) }))] })] }, m.id));
        }) }));
}
/**
 * RenderedContent renders the message body. For user messages we use a
 * simple <pre>-wrapped escape; for assistant messages we render the
 * model's markdown via a tiny inline renderer (bold + italic + headings +
 * inline code + lists + blockquote + paragraphs). Pulling react-markdown
 * later if we need full GFM; this minimal renderer covers ~95% of what
 * agent responses contain and avoids a dependency for this milestone.
 */
function RenderedContent({ content, role }) {
    if (role === 'user') {
        return _jsx("div", { className: "message-text user-text", children: content });
    }
    return _jsx("div", { className: "message-text assistant-text", dangerouslySetInnerHTML: { __html: minimalMarkdown(content) } });
}
/**
 * minimalMarkdown -- intentionally tiny, dependency-free markdown subset.
 * Handles: ATX headings, bold (**), italic (*), inline code (`), unordered
 * lists (-), ordered lists (1.), blockquotes (>), horizontal rules (---),
 * code fences (```), paragraphs.
 *
 * Escapes raw HTML first so user content can't inject. Later we can swap
 * in react-markdown + remark-gfm without changing the call site.
 */
function minimalMarkdown(src) {
    let s = src
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    // Code fences first so their contents aren't parsed.
    s = s.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _lang, body) => {
        return `<pre class="md-code"><code>${body}</code></pre>`;
    });
    // Headings
    s = s.replace(/^### (.*)$/gm, '<h3>$1</h3>');
    s = s.replace(/^## (.*)$/gm, '<h2>$1</h2>');
    s = s.replace(/^# (.*)$/gm, '<h1>$1</h1>');
    // Horizontal rule
    s = s.replace(/^---$/gm, '<hr/>');
    // Blockquote
    s = s.replace(/^&gt; (.*)$/gm, '<blockquote>$1</blockquote>');
    // Inline code (avoid greedy across pre blocks)
    s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>');
    // Bold
    s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
    // Italic (single *)
    s = s.replace(/(^|\W)\*([^*\n]+)\*(\W|$)/g, '$1<em>$2</em>$3');
    // Lists
    s = s.replace(/(^|\n)(- .*(?:\n- .*)*)/g, (_m, lead, block) => {
        const items = block.split('\n').map((line) => `<li>${line.replace(/^- /, '')}</li>`).join('');
        return `${lead}<ul>${items}</ul>`;
    });
    s = s.replace(/(^|\n)(\d+\. .*(?:\n\d+\. .*)*)/g, (_m, lead, block) => {
        const items = block.split('\n').map((line) => `<li>${line.replace(/^\d+\. /, '')}</li>`).join('');
        return `${lead}<ol>${items}</ol>`;
    });
    // Paragraphs: any remaining double-newlined chunk becomes <p>
    const blocks = s.split(/\n\n+/);
    return blocks
        .map((b) => {
        const trimmed = b.trim();
        if (!trimmed)
            return '';
        // Already a block-level tag? Pass through.
        if (/^<(h\d|pre|ul|ol|blockquote|hr|p)/i.test(trimmed))
            return trimmed;
        return `<p>${trimmed.replace(/\n/g, '<br/>')}</p>`;
    })
        .join('\n');
}
